import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { Stack, Typography, Box, Stepper, StepLabel, Step } from "@mui/material";

import { ReactComponent as ArrowleftIcon } from "../../../../vendor/_new/icon/sites/arrow_left.svg";
import { ReactComponent as ActivestepIcon } from "../../../../vendor/_new/icon/sites/active_step.svg";
import { ReactComponent as InactivestepIcon } from "../../../../vendor/_new/icon/sites/inactive_step.svg";
import { ReactComponent as CompletestepIcon } from "../../../../vendor/_new/icon/sites/complete_step.svg";
import { ReactComponent as InfoIcon } from "../../../../vendor/_new/icon/sites/info.svg";

import WebsiteInformation from "./WebsiteInformation";
import SSLConfiguration from "./SSLConfiguration";
import WAFConfiguration from "./WAFConfiguration";
import ReviewConfiguration from "./ReviewConfiguration";
import ConfigurationProcess from "./ConfigurationProcess";
import Finalize from "./Finalize";

import colors from "../../../../utils/colors";

const AddSitesPage = () => {
  const navigate = useNavigate();
  const steps = [
    "Website  information",
    "SSL configuration",
    "WAF configuration",
    "Review configuration",
    "Configuration process",
    "Finalize",
  ];
  const [activeStep, setActiveStep] = useState(0);
  const [siteUid, setSiteUid] = useState();

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };
  const handlePrev = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  return (
    <>
      <Helmet title="Add domain" />
      <Box sx={{ p: 6 }}>
        <Stack spacing={6}>
          <Stack direction={"row"} alignItems={"center"} spacing={2}>
            <Stack direction={"row"} alignItems={"center"} spacing={2} onClick={() => navigate("/v2/sites")} sx={{ cursor: "pointer" }}>
              <ArrowleftIcon style={{ color: colors.primary[300] }} />
              <Typography sx={ { fontSize: 16, lineHeight: "24px", color: colors.primary[300], fontWeight: "medium" }}>
                Sites
              </Typography>
            </Stack>
            <Typography sx={ { fontSize: 16, lineHeight: "24px", color: colors.neutral[1000], fontWeight: "medium" }}>
              /
            </Typography>
            <Typography sx={ { fontSize: 16, lineHeight: "24px", color: colors.neutral[1000], fontWeight: "medium" }}>
              Add domain
            </Typography>
          </Stack>
          <Box sx={{ width: "100%" }}>
            <Stepper activeStep={activeStep}>
              {steps.map((label, index) => {
                return (
                  <Step key={`step${index}`}>
                    <StepLabel
                      icon={index === activeStep ? <ActivestepIcon /> : index < activeStep ? <CompletestepIcon /> : <InactivestepIcon />}
                    >
                      <Typography
                        sx={{
                          fontSize: 14,
                          lineHeight: "21px",
                          color: index === activeStep ? colors.neutral[1000] : colors.neutral[600],
                          fontWeight: index === activeStep ? "medium" : "regular",
                        }}
                      >
                        {label}
                      </Typography>
                    </StepLabel>
                  </Step>
                );
              })}
            </Stepper>
          </Box>
          <Stack direction="row" spacing={6} sx={{ pt: 4, alignItems: "stretch" }}>
            <Box sx={{ width: "70%", display: "flex", flexDirection: "column" }}>
              {activeStep === 0 && <WebsiteInformation handleNext={handleNext} setSiteUid={setSiteUid} />}
              {activeStep === 1 && <SSLConfiguration handleNext={handleNext} siteUid={siteUid} />}
              {activeStep === 2 && <WAFConfiguration handleNext={handleNext} handlePrev={handlePrev} siteUid={siteUid} />}
              {activeStep === 3 && <ReviewConfiguration handleNext={handleNext} handlePrev={handlePrev} />}
              {activeStep === 4 && <ConfigurationProcess handleNext={handleNext} handlePrev={handlePrev} />}
              {activeStep === 5 && <Finalize handlePrev={handlePrev} />}
            </Box>
            <Box
              sx={{
                width: "30%",
                display: activeStep === 4 ? "none" : "flex",
                alignItems: "flex-start",
                gap: 4,
                p: 4,
                backgroundColor: "#FFFFFF",
                border: `1px solid ${colors.primary[300]}`,
                borderRadius: "8px",
                height: "fit-content",
                marginTop: activeStep === 1 ? "45px !important" : activeStep === 2 ? "45px !important" : activeStep === 3 ? "45px !important" : activeStep === 5 ? "35px !important" : 0,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <InfoIcon style={{ width: 32, height: 32, color: colors.primary[300] }} />
              </Box>
              <Stack spacing={3}>
                {activeStep === 0 && (
                  <>
                    <Typography
                      sx={{
                        fontSize: 14,
                        lineHeight: "21px",
                        color: colors.primary[300],
                      }}
                    >
                      Enter the website domain name without 'www'. Layerd provides the 'www' subdomain by default, so there's no need to configure it separately.
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 14,
                        lineHeight: "21px",
                        color: colors.primary[300],
                      }}
                    >
                      Provide the actual origin IP address where the clean requests need to be sent.
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 14,
                        lineHeight: "21px",
                        color: colors.primary[300],
                      }}
                    >
                      Layerd will perform a health check of the website and origin server before accepting the registration. Ensure that the domain is active and the origin server is reachable before adding the site to Layerd.
                    </Typography>
                  </>
                )}
                {activeStep === 1 && (
                  <>
                    <Typography
                      sx={{
                        fontSize: 14,
                        lineHeight: "21px",
                        color: colors.primary[300],
                      }}
                    >
                      At this step, you will be configuring SSL settings for your web applications. Select the appropriate settings. Refer to the SSL configuration Guide for more information.
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 14,
                        lineHeight: "21px",
                        color: colors.primary[300],
                      }}
                    >
                      If you want to enable "Adaptive, Adaptive E2E or Advanced E2E" mode, ensure you have generated/uploaded an SSL certificate before continuing.
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 14,
                        lineHeight: "21px",
                        color: colors.primary[300],
                      }}
                    >
                      To install SSL certificate, you will have the option to Universal or Upload SSL certificate (your own).
                    </Typography>
                  </>
                )}
                {activeStep === 2 && (
                  <>
                    <Typography
                      sx={{
                        fontSize: 14,
                        lineHeight: "21px",
                        color: colors.primary[300],
                      }}
                    >
                      On this screen, you can enable WAF configurations according to your security levels.
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 14,
                        lineHeight: "21px",
                        color: colors.primary[300],
                      }}
                    >
                      These are layered (Defence in depth) and will not conflict.
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 14,
                        lineHeight: "21px",
                        color: colors.primary[300],
                      }}
                    >
                      Click on 'Continue'.
                    </Typography>
                  </>
                )}
                {activeStep === 3 && (
                  <>
                    <Typography
                      sx={{
                        fontSize: 14,
                        lineHeight: "21px",
                        color: colors.primary[300],
                      }}
                    >
                      Please review the configuration before deploying into Layerd Edges globally.
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 14,
                        lineHeight: "21px",
                        color: colors.primary[300],
                      }}
                    >
                      HTTPS redirection will be enabled automatically if the SSL is enabled on previous screen. You can disable it later from SSL settings if required.
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 14,
                        lineHeight: "21px",
                        color: colors.primary[300],
                      }}
                    >
                      WAF will be deployed in Audit mode and make sure you enforce it from WAF configuration page.
                    </Typography>
                  </>
                )}
                {activeStep === 4 && (
                  <Typography
                    sx={{
                      fontSize: 14,
                      lineHeight: "21px",
                      color: colors.primary[300],
                    }}
                  >
                    Follow the steps to complete your site configuration.
                  </Typography>
                )}
                {activeStep === 5 && (
                  <>
                    <Typography
                      sx={{
                        fontSize: 14,
                        lineHeight: "21px",
                        color: colors.primary[300],
                      }}
                    >
                      Create or update the A record for your domain (naked/bare domain) to one of Layerd AI Anycast IP Addresses.
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 14,
                        lineHeight: "21px",
                        color: colors.primary[300],
                      }}
                    >
                      Create or update the CNAME record for your www subdomain to the provided CNAME record.
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 14,
                        lineHeight: "21px",
                        color: colors.primary[300],
                      }}
                    >
                      If you are going to use multiple subdomains for your website, please create or update the CNAME records for all of the subdomains individually after onboarding to Layerd AI.
                    </Typography>
                  </>
                )}
              </Stack>
            </Box>
          </Stack>
        </Stack>
      </Box>
    </>
  );
};

export default AddSitesPage;
